/**
 * Live Impact panel — the verdict block inside a Squad Planning action modal.
 *
 * Renders, for the whole scenario after the action being edited: a state chip,
 * the three financial deltas, one headline per compliance layer, and the link
 * through to the full impact view (SE-7002, FC-1.1; Figma 10:2 breach, 36:36
 * compliant, 159:124 awaiting input, 141:653 dark).
 *
 * THE PANEL NEVER GATES CONFIRM. It reports; it does not decide. There is no
 * disabled state, no confirmation step and no warning interstitial anywhere in
 * this file, and the standing note it renders in every state says so out loud
 * — which is also why that note lives here rather than in each modal's footer:
 * its wording changes with the panel's own state.
 * @module features/financial-compliance/components/molecules/LiveImpactPanel
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Divider } from "@/components/atoms/Divider";
import { Dot } from "@/components/atoms/Dot";
import { Stack } from "@/components/atoms/Stack";
import { StatusPill } from "@/components/atoms/StatusPill";
import { Text } from "@/components/atoms/Text";

import { verdictChipLabel } from "../../../domain/verdict";

import type { StatusPillTone } from "@/components/atoms/StatusPill";
import type {
  LayerHeadline,
  LiveImpactModel,
  RuleState,
  ScenarioImpactVerdict,
} from "../../../types";

/** Props for {@link LiveImpactPanelView}. */
export interface LiveImpactPanelViewProps {
  /** What to render — a closed union, never a bag of optionals. */
  readonly model: LiveImpactModel;
  /** Opens the full impact view for the current scenario. */
  readonly onViewFullImpact: () => void;
  /** Test identity prefix, so four modals can host distinct panels. */
  readonly testID?: string;
}

/** Marker fill per rule state; an unconfigured rule gets no status colour. */
const DOT_CLASS: Record<RuleState, string> = {
  breach: "bg-status-error",
  "at-risk": "bg-status-warning",
  "on-track": "bg-status-success",
  "awaiting-data": "bg-content-muted",
  "not-configured": "bg-content-muted",
};

/** Headline colour: an unconfigured layer recedes rather than asserting. */
const HEADLINE_CLASS: Record<RuleState, string> = {
  breach: "text-content-primary",
  "at-risk": "text-content-primary",
  "on-track": "text-content-primary",
  "awaiting-data": "text-content-muted",
  "not-configured": "text-content-muted",
};

/**
 * Chip tone per verdict state.
 *
 * `incomplete` is WARNING rather than neutral on purpose. It means some rule
 * could not be fully computed, so the verdict is a lower bound — the true
 * position can only be worse. A neutral chip would read as "nothing to see",
 * which is the misreading SE-8378 exists to prevent; a success chip would be a
 * claim of compliance nobody has established.
 */
const CHIP_TONE: Record<ScenarioImpactVerdict["state"], StatusPillTone> = {
  breach: "error",
  ok: "success",
  incomplete: "warning",
  "not-configured": "neutral",
};

/**
 * Renders one headline row.
 * @param headline - The layer headline.
 * @param testID - Panel test id prefix.
 * @returns The row element.
 */
const renderHeadline = (
  headline: LayerHeadline,
  testID: string
): React.ReactElement => (
  <Stack
    key={headline.layerId}
    direction="horizontal"
    className="items-center gap-2"
  >
    <Dot className={DOT_CLASS[headline.state]} />
    <Text
      variant="caption"
      testID={`${testID}-headline-${headline.layerId}`}
      className={HEADLINE_CLASS[headline.state]}
    >
      {headline.headline}
    </Text>
  </Stack>
);

/**
 * What the headline list says when the club has configured no rules at all.
 *
 * The rows are one-per-layer reductions of the club's own rule registry, so a
 * club with nothing set up produces none — and an empty gap under the divider
 * would read as a clean bill of health. This says the true thing instead: there
 * is nothing to judge against. It is prose about the absence, never a
 * synthesised rule, because inventing a row would put a verdict on a rule that
 * does not exist.
 * @param testID - Panel test id prefix.
 * @returns The row element.
 */
const renderNoRules = (testID: string): React.ReactElement => (
  <Text
    variant="caption"
    testID={`${testID}-no-rules`}
    className="text-content-muted"
  >
    No compliance rules are configured for this club yet.
  </Text>
);

/**
 * How a verdict that is one keystroke out of date is shown.
 *
 * Per the UI decision on SE-8458: the previous answer STAYS, dimmed, until the
 * new one arrives. No spinner, no skeleton, no "updating" label, and above all
 * no layout shift — the same elements are on screen either way and only their
 * opacity changes, so nothing appears, disappears or moves while the user is
 * still typing.
 *
 * This deliberately does NOT dim the financial deltas. Those are computed from
 * the terms in the modal, owe nothing to the network, and are already correct
 * the instant a key lands — dimming them would claim a staleness they do not
 * have.
 * @param isPending - Whether a newer verdict is in flight.
 * @returns The opacity class for the stale content.
 */
const settlingClass = (isPending: boolean): string =>
  isPending ? "opacity-60" : "opacity-100";

/**
 * The panel before any terms have been entered.
 * @param testID - Panel test id prefix.
 * @returns The empty-state element.
 */
const renderAwaitingInput = (testID: string): React.ReactElement => (
  // The design frames a glyph in a ringed disc above this copy, but the glyph
  // is exported as a flattened asset and the ring's diameter and radius are
  // both unbound, so neither is reproducible without inventing values. The copy
  // is bound and carries the whole message on its own; the disc is on the
  // design bind-list rather than approximated here.
  <Stack className="items-center justify-center gap-2 py-6">
    <Text
      variant="body"
      testID={`${testID}-empty-title`}
      className="text-content-secondary"
    >
      Impact appears as you enter terms
    </Text>
    <Text variant="caption" className="text-center text-content-muted">
      {/* SE-8812: these action modals (Sign, Sell, Loan, Promote) open in a
       * scenario only — Current Squad is read-only and offers "Add Annual
       * Salary" alone — so the scenario can be named unconditionally, no
       * prop needed. Since SE-8747 the checks run against the scenario,
       * not the current squad; this line said the opposite. */}
      SCR, PSR, wage budget and registration checks run live against this
      scenario.
    </Text>
  </Stack>
);

/**
 * Renders the "some loan has no recorded covered share" disclosure.
 *
 * It sits DIRECTLY under the deltas, not down beside the standing note, because
 * it qualifies the wage figure immediately above it: a reader who takes in the
 * number and looks away must have already passed the sentence saying the number
 * is short. Muted, because it reports an absence rather than an alert — there
 * is nothing wrong with the scenario, only something missing from an old loan.
 * @param note - The disclosure, already worded by the domain.
 * @param testID - Panel test id prefix.
 * @returns The row element, or null when every loan has a recorded share.
 */
const renderUnrecordedShareNote = (
  note: string | null,
  testID: string
): React.ReactElement | null =>
  note == null ? null : (
    <Text
      variant="caption"
      testID={`${testID}-unrecorded-share-note`}
      className="text-content-muted"
    >
      {note}
    </Text>
  );

/**
 * The verdict a model carries, or null when there is nothing to judge yet.
 * @param model - The panel model.
 * @returns The verdict, or null.
 */
const verdictOf = (model: LiveImpactModel): ScenarioImpactVerdict | null =>
  model.kind === "verdict" ? model.verdict : null;

/**
 * Renders the panel body for a permitted tier.
 * @param verdict - The verdict, or null while awaiting input.
 * @param onViewFullImpact - Opens the full impact view.
 * @param testID - Test identity prefix.
 * @returns The panel element.
 */
const renderPanel = (
  verdict: ScenarioImpactVerdict | null,
  onViewFullImpact: () => void,
  testID: string
): React.ReactElement => (
  <Box
    testID={testID}
    // Every value here is a variable bound in the design node: surface/subtle,
    // space/6, space/5, radius/none. The square corner is the design's, not an
    // omission — no radius variable other than radius/none is bound anywhere
    // in this subtree.
    className="w-full rounded-none bg-surface-subtle px-6 py-5"
  >
    <Stack className="gap-4">
      <Stack
        direction="horizontal"
        className="items-center justify-between gap-2"
      >
        <Text
          variant="micro"
          className="uppercase tracking-wide text-content-muted"
        >
          Live impact
        </Text>
        {verdict ? (
          <Stack
            direction="horizontal"
            testID={`${testID}-verdict-chip`}
            className={`items-center gap-2 ${settlingClass(verdict.isPending)}`}
          >
            <StatusPill
              testID={`${testID}-state`}
              tone={CHIP_TONE[verdict.state]}
              label={verdictChipLabel(verdict)}
            />
          </Stack>
        ) : null}
      </Stack>

      {verdict ? (
        <Stack className="gap-2">
          {verdict.deltas.map(delta => (
            <Stack
              key={delta.id}
              direction="horizontal"
              className="items-center justify-between gap-2"
            >
              <Text variant="caption" className="text-content-secondary">
                {delta.label}
              </Text>
              <Text
                variant="body-strong"
                testID={`${testID}-delta-${delta.id}`}
                className="text-content-primary"
              >
                {delta.value}
              </Text>
            </Stack>
          ))}
        </Stack>
      ) : (
        renderAwaitingInput(testID)
      )}

      {verdict
        ? renderUnrecordedShareNote(verdict.unrecordedShareNote, testID)
        : null}

      {verdict ? <Divider className="bg-content-disabled" /> : null}

      {verdict ? (
        <Stack
          testID={`${testID}-headlines`}
          className={`gap-2 ${settlingClass(verdict.isPending)}`}
        >
          {verdict.headlines.length === 0
            ? renderNoRules(testID)
            : verdict.headlines.map(headline =>
                renderHeadline(headline, testID)
              )}
        </Stack>
      ) : null}

      {verdict ? (
        <Button
          testID={`${testID}-view-full`}
          variant="link"
          size="sm"
          labelTone="accent"
          labelVariant="caption"
          label="View full impact →"
          onPress={onViewFullImpact}
        />
      ) : null}

      <Text
        variant="caption"
        testID={`${testID}-never-blocked-note`}
        className="text-content-muted"
      >
        {verdict
          ? "Informational — Confirm is never blocked"
          : "Checks run as you type — Confirm is never blocked"}
      </Text>
    </Stack>
  </Box>
);

/**
 * Renders the Live Impact panel.
 * @param props - Component props
 * @param props.model - What to render
 * @param props.onViewFullImpact - Opens the full impact view
 * @param props.testID - Test identity prefix
 * @returns The panel element, or null when the tier does not include it.
 */
const LiveImpactPanelView = ({
  model,
  onViewFullImpact,
  testID = "live-impact",
}: LiveImpactPanelViewProps) =>
  // A tier that does not include this surface renders NOTHING — no empty slot
  // and no call to action, either of which would disclose that it exists.
  model.kind === "hidden"
    ? null
    : renderPanel(verdictOf(model), onViewFullImpact, testID);

LiveImpactPanelView.displayName = "LiveImpactPanelView";

export default memo(LiveImpactPanelView);
